import { supabase } from './supabaseClient';
import { compressImageForUpload, LOGO_UPLOAD_OPTIONS } from '@tradesmatepro/shared-ui';

/**
 * Upload a company branding asset (logo, letterhead…) and return its public URL.
 *
 * ONE implementation, deliberately. The logic previously lived inline in
 * CompanyProfileSettingsTab.handleFileUpload and nowhere else, so onboarding had no way to
 * collect a logo without copying it — and a second copy of an upload path means a second
 * bucket name, a second folder convention and a second set of storage-policy assumptions
 * to keep in step. Storage is invisible to every guard in this repo (no linting, no schema
 * check, `ai_readonly` cannot read schema `storage`), so a divergence here would be found
 * by a customer, not by CI. See docs/STORAGE_MODEL.md.
 *
 * Bucket and path are unchanged from the original: `company-assets`, keyed
 * `company-<id>/branding/<fileType>/<timestamp>-<rand>.<ext>`.
 *
 * @param {File} file
 * @param {string} companyId
 * @param {string} fileType  e.g. 'company_logo'
 * @returns {Promise<string|null>} public URL, or null with the reason thrown
 */
export const uploadCompanyAsset = async (file, companyId, fileType = 'company_logo') => {
  if (!file) return null;
  if (!companyId) throw new Error('Cannot upload a company asset without a company id');

  if (!file.type.startsWith('image/')) {
    throw new Error('Please select an image file (PNG, JPG, GIF)');
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('File size must be less than 5MB');
  }
  // Ruling 4 (2026-11-02): large logos are downscaled at high quality; small ones pass through.
  file = await compressImageForUpload(file, LOGO_UPLOAD_OPTIONS);

  const ext = file.name.split('.').pop();
  const randomId = Math.random().toString(36).substring(2, 8);
  const filePath = `company-${companyId}/branding/${fileType}/${Date.now()}-${randomId}.${ext}`;

  const { error } = await supabase.storage
    .from('company-assets')
    .upload(filePath, file, { upsert: true });

  if (error) throw error;

  const { data } = supabase.storage.from('company-assets').getPublicUrl(filePath);
  return data?.publicUrl || null;
};

/**
 * The canonical column for a company's logo is `companies.company_logo_url`.
 *
 * `companies.logo_url` also exists and is populated on 0 of 186 companies (verified live
 * 2026-08-28) while company_logo_url is set on 47 — the profile page reads and writes
 * company_logo_url and only uses `logo_url` as a local form key. Exported so nothing has
 * to re-derive which of the two is real.
 */
export const COMPANY_LOGO_COLUMN = 'company_logo_url';
